/**
 * Cart Drawer — Susmies
 * Features: reservation timer, free shipping bar, upsell carousel (drag),
 * AJAX qty/remove/upsell, self re-render via Section Rendering API.
 */
(function () {
  'use strict';

  const SECTION_ID = 'iurop-cart-drawer';
  const RESERVATION_KEY = 'iurop:cart-reservation-expiry';
  const RESERVATION_EXPIRED_KEY = 'iurop:cart-reservation-expired';

  const qs = (sel, root) => (root || document).querySelector(sel);
  const qsa = (sel, root) => Array.from((root || document).querySelectorAll(sel));

  const updateCartCountUI = (count) => {
    qsa('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('visually-hidden', count === 0);
    });
  };

  const openDrawer = () => {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
  };

  const closeDrawer = () => {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
  };

  // ------------------------------------------------------------------
  // Reservation timer
  // ------------------------------------------------------------------
  let reservationInterval = null;

  const formatMMSS = (seconds) => {
    if (seconds < 0) seconds = 0;
    const mm = Math.floor(seconds / 60);
    const ss = seconds % 60;
    return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  };

  const initReservation = () => {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    const enabled = drawer.dataset.reservationEnabled === 'true';
    const minutes = parseInt(drawer.dataset.reservationMinutes, 10) || 10;
    const timerEl = qs('[data-reservation-timer]');
    if (!enabled || !timerEl) return;

    if (reservationInterval) {
      clearInterval(reservationInterval);
      reservationInterval = null;
    }

    const reservationEl = qs('[data-reservation]');
    const expireBanner = () => {
      if (!reservationEl) return;
      const expired = (window.theme && window.theme.t && window.theme.t.cart && window.theme.t.cart.reservationExpired) || 'TIEMPO EXPIRADO, ULTIMA OPORTUNIDAD';
      reservationEl.classList.add('is-expired');
      reservationEl.innerHTML =
        '<span class="cart-drawer__reservation-text">' + expired + '</span>';
    };

    // If a previous timer already expired in this session, keep showing the
    // expired banner instead of restarting the countdown — until the cart
    // empties (which clears the flag).
    let alreadyExpired = false;
    try { alreadyExpired = localStorage.getItem(RESERVATION_EXPIRED_KEY) === '1'; } catch (_) {}
    if (alreadyExpired) {
      expireBanner();
      return;
    }

    // Get or create expiry timestamp in localStorage.
    let expiry = null;
    try {
      const stored = parseInt(localStorage.getItem(RESERVATION_KEY), 10);
      if (stored && stored > Date.now()) expiry = stored;
    } catch (_) {}
    if (!expiry) {
      expiry = Date.now() + minutes * 60 * 1000;
      try { localStorage.setItem(RESERVATION_KEY, String(expiry)); } catch (_) {}
    }

    const tick = () => {
      const remaining = Math.floor((expiry - Date.now()) / 1000);
      if (remaining <= 0) {
        expireBanner();
        clearInterval(reservationInterval);
        reservationInterval = null;
        try {
          localStorage.removeItem(RESERVATION_KEY);
          localStorage.setItem(RESERVATION_EXPIRED_KEY, '1');
        } catch (_) {}
        return;
      }
      timerEl.textContent = formatMMSS(remaining);
    };
    tick();
    reservationInterval = setInterval(tick, 1000);
  };

  // Reset reservation when cart empties.
  const maybeClearReservation = (cart) => {
    if (cart && cart.item_count === 0) {
      try {
        localStorage.removeItem(RESERVATION_KEY);
        localStorage.removeItem(RESERVATION_EXPIRED_KEY);
      } catch (_) {}
    }
  };

  // ------------------------------------------------------------------
  // Re-render the whole drawer from the server.
  // ------------------------------------------------------------------
  const refreshDrawer = () => {
    return fetch(`/?sections=${SECTION_ID}`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((data) => {
        const html = data[SECTION_ID];
        if (!html) return;
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newDrawer = doc.querySelector('[data-cart-drawer]');
        const currentDrawer = qs('[data-cart-drawer]');
        if (newDrawer && currentDrawer) {
          const wasOpen = currentDrawer.classList.contains('is-open');
          currentDrawer.innerHTML = newDrawer.innerHTML;
          if (wasOpen) currentDrawer.classList.add('is-open');
          bindEvents();
          initReservation();
        }
      })
      .catch((err) => console.error('[cart-drawer] refresh failed', err));
  };

  // ------------------------------------------------------------------
  // Cart AJAX
  // ------------------------------------------------------------------
  const changeLineQty = (lineKey, newQty) => {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: newQty }),
    })
      .then((r) => r.json())
      .then((cart) => {
        updateCartCountUI(cart.item_count);
        maybeClearReservation(cart);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, skipDrawerRefresh: true } }));
        return refreshDrawer();
      });
  };

  const addUpsell = (btn) => {
    const variantId = btn.dataset.variantId;
    if (!variantId || btn.disabled) return;
    btn.disabled = true;
    const originalText = btn.textContent;
    const tCart = (window.theme && window.theme.t && window.theme.t.cart) || {};
    btn.textContent = tCart.adding || 'Añadiendo…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 }),
    })
      .then((r) => (r.ok ? r.json() : r.json().then((e) => Promise.reject(e))))
      .then(() => fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()))
      .then((cart) => {
        updateCartCountUI(cart.item_count);
        btn.classList.add('is-added');
        btn.textContent = tCart.added || 'Añadido';
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, skipDrawerRefresh: true } }));
        return refreshDrawer();
      })
      .catch((err) => {
        console.error('[cart-drawer] add upsell failed', err);
        btn.disabled = false;
        btn.textContent = originalText;
      });
  };

  // ------------------------------------------------------------------
  // Upsell carousel — drag + progress sync
  // ------------------------------------------------------------------
  const initUpsellCarousel = () => {
    const track = qs('[data-upsells-track]');
    if (!track) return;

    const segments = qsa('.cart-drawer__upsells-progress-segment');
    const cards = qsa('[data-upsell-row]', track);

    // Drag support (pointer events)
    let isDown = false;
    let startX = 0;
    let startScrollLeft = 0;
    let moved = false;

    track.addEventListener('pointerdown', (e) => {
      if (e.button !== undefined && e.button !== 0) return;
      isDown = true;
      moved = false;
      startX = e.clientX;
      startScrollLeft = track.scrollLeft;
    });
    track.addEventListener('pointermove', (e) => {
      if (!isDown) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 4) {
        moved = true;
        track.classList.add('is-dragging');
        // Capture only after a real drag begins, otherwise pointer capture
        // redirects the subsequent `click` event away from the button and
        // breaks the upsell "Añadir" handler.
        try { track.setPointerCapture(e.pointerId); } catch (_) {}
      }
      if (moved) track.scrollLeft = startScrollLeft - dx;
    });
    const endDrag = (e) => {
      if (!isDown) return;
      isDown = false;
      track.classList.remove('is-dragging');
      if (moved) {
        try { track.releasePointerCapture(e.pointerId); } catch (_) {}
      }
    };
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('pointerleave', endDrag);
    track.addEventListener('click', (e) => {
      if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
    }, true);

    // Progress bar sync — IntersectionObserver per card
    if (segments.length && cards.length && 'IntersectionObserver' in window) {
      const setActive = (idx) => {
        segments.forEach((s, i) => s.classList.toggle('is-active', i === idx));
      };
      const observer = new IntersectionObserver(
        (entries) => {
          let best = null;
          entries.forEach((entry) => {
            if (!best || entry.intersectionRatio > best.intersectionRatio) best = entry;
          });
          if (best && best.isIntersecting) {
            const idx = cards.indexOf(best.target);
            if (idx >= 0) setActive(idx);
          }
        },
        { root: track, threshold: [0.5, 0.75, 1] }
      );
      cards.forEach((c) => observer.observe(c));
    }
  };

  // ------------------------------------------------------------------
  // Bind events after each render
  // ------------------------------------------------------------------
  const bindEvents = () => {
    qsa('[data-cart-close]').forEach((el) => el.addEventListener('click', closeDrawer));

    qsa('[data-qty-control]').forEach((ctrl) => {
      const input = ctrl.querySelector('[data-qty-input]');
      if (!input) return;
      ctrl.querySelectorAll('[data-qty-change]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const delta = parseInt(btn.dataset.qtyChange, 10);
          const newVal = Math.max(0, parseInt(input.value, 10) + delta);
          input.value = newVal;
          changeLineQty(input.dataset.lineKey, newVal);
        });
      });
      input.addEventListener('change', () => {
        const val = Math.max(0, parseInt(input.value, 10) || 0);
        changeLineQty(input.dataset.lineKey, val);
      });
    });

    qsa('[data-cart-remove]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const row = btn.closest('.cart-drawer__item');
        if (row) row.classList.add('is-removing');
        changeLineQty(btn.dataset.lineKey, 0);
      });
    });

    initUpsellCarousel();
  };

  // Delegated handler for upsell "Añadir" — survives drawer re-renders.
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-upsell-add]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    addUpsell(btn);
  });

  // ------------------------------------------------------------------
  // Global init
  // ------------------------------------------------------------------
  const init = () => {
    bindEvents();
    initReservation();

    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('#cart-icon-bubble, #cart-icon-bubble-mobile, [data-cart-open]');
      if (!trigger) return;
      e.preventDefault();
      openDrawer();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const drawer = qs('[data-cart-drawer]');
        if (drawer && drawer.classList.contains('is-open')) closeDrawer();
      }
    });

    document.addEventListener('cart:updated', (e) => {
      if (e.detail && e.detail.skipDrawerRefresh) return;
      refreshDrawer().then(() => {
        if (e.detail && e.detail.openDrawer !== false) openDrawer();
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('[data-cart-drawer]')) {
      bindEvents();
      initReservation();
    }
  });
})();
